/**
 * 将 API 商品/案例数据按当前语言展开
 * 用途：替代原先从 i18n/locales 读取 products.* / cases.items.* 的方式
 * 用法：localizeProductFromApi(item, locale, t)
 */

import { resolveGallery, resolveMediaUrl } from '@/services/api'

/**
 * 取类别多语言名称
 * 用法：categoryLabel(categoryObj, 'zh-CN')
 */
export function categoryLabel(category, locale = 'zh-CN') {
  if (!category) return ''
  if (typeof category === 'string') return category
  return (
    category.i18n?.[locale]?.name ||
    category.i18n?.['zh-CN']?.name ||
    category.i18n?.en?.name ||
    category.id ||
    ''
  )
}

/** 根据 categoryKey 在类别列表中查找显示名 */
export function resolveCategoryLabel(categories, categoryKey, locale, fallbackT) {
  const found = (categories || []).find((c) => c.id === categoryKey)
  if (found) return categoryLabel(found, locale)
  // 兼容旧数据：回退到前端 i18n 词条
  if (fallbackT && categoryKey) {
    const key = `categories.${categoryKey}`
    const label = fallbackT(key)
    return label === key ? categoryKey : label
  }
  return categoryKey || ''
}

/**
 * @param {object} item API 商品
 * @param {string} locale 如 zh-CN
 * @param {Function} t i18n t 函数
 * @param {Array} categories 可选，API 类别列表
 */
export function localizeProductFromApi(item, locale, t, categories = []) {
  if (!item) return null
  const lang = (item.i18n && (item.i18n[locale] || item.i18n['zh-CN'] || item.i18n.en)) || {}
  return {
    ...item,
    src: resolveMediaUrl(item.src),
    gallery: resolveGallery(item.gallery),
    name: lang.name || item.id,
    category: resolveCategoryLabel(categories, item.categoryKey, locale, t),
    moq: lang.moq || '',
    material: lang.material || '',
    leadTime: lang.leadTime || '',
    description: lang.description || '',
    specs: Array.isArray(lang.specs) ? lang.specs : []
  }
}

export function localizeCaseFromApi(item, locale, t, categories = []) {
  if (!item) return null
  const lang = (item.i18n && (item.i18n[locale] || item.i18n['zh-CN'] || item.i18n.en)) || {}
  return {
    ...item,
    cover: resolveMediaUrl(item.cover),
    gallery: resolveGallery(item.gallery),
    title: lang.title || item.id,
    client: lang.client || '',
    summary: lang.summary || '',
    challenge: lang.challenge || '',
    solution: lang.solution || '',
    result: lang.result || '',
    highlights: Array.isArray(lang.highlights) ? lang.highlights : [],
    category: resolveCategoryLabel(categories, item.categoryKey, locale, t),
    metricLabels: {
      saving: t('cases.metrics.saving'),
      units: t('cases.metrics.units'),
      leadTime: t('cases.metrics.leadTime')
    }
  }
}

export function getCategoryKeysFromList(list = []) {
  const keys = []
  list.forEach((item) => {
    if (item.categoryKey && !keys.includes(item.categoryKey)) {
      keys.push(item.categoryKey)
    }
  })
  return keys
}
